import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import SVG from 'react-inlinesvg';
import { useTranslation } from 'react-i18next';

import { PhoneFrame } from '../../assets';
import Text from '../Text';
import { ARCHIVE } from '../../containers/campaigns/components/Tiles/utils/constants';
import { normalizeSmsText } from '../../containers/broadcastMessages/utils/textHelpers';
import defaultAttrs from '../../lib/utils/defaultAttrs';

// Matches SmsOptOutFooter::FOOTER, the line texts end with for orgs that have
// the opt-out footer turned on.
const OPT_OUT_FOOTER = 'Reply STOP to opt-out.';

/**
 * A campaign message's text on a phone: a link card for the post's first
 * finished tile, then the text as sent, without the link it ends with.
 * The ref gets the phone alone, which is what Download Preview draws.
 */
export const SmsPreview = forwardRef(({ boardName, subject, postId }, ref) => {
  const { t } = useTranslation('posts');
  const smsOptOutFooter = useSelector(
    state => state.currentBoard.smsOptOutFooter,
  );

  const tiles = useSelector(state => state.campaignDnd[`post-${postId}`]);
  const visibleTiles = tiles.filter(
    tile => tile.fullyAssembled && tile.tileStatus !== ARCHIVE,
  );

  const tile = visibleTiles[0];

  // Normalized the way Sms::Normalizer does before sending.
  const text = normalizeSmsText(
    subject
      ? t('reviewModal.smsPreview.subjectPreview', {
          boardName,
          subject,
        })
      : t('reviewModal.smsPreview.defaultSubjectPreview', { boardName }),
  );
  const previewSubject = smsOptOutFooter ? `${text}\n${OPT_OUT_FOOTER}` : text;

  return (
    <Wrapper>
      <PhoneBox ref={ref}>
        <Phone src={PhoneFrame} />
        <Content>
          <ChatMessages>
            {tile ? (
              <>
                <TextWrapper>
                  <Thumbnail src={tile.thumbnail} />
                  <Headline paddingBottom="0px">{tile.headline}</Headline>
                  <BubbleText color="black.80" fontSize="12px">
                    {t('reviewModal.smsPreview.airboLink')}
                  </BubbleText>
                </TextWrapper>

                <TextWrapper>
                  <BubbleText>{previewSubject}</BubbleText>
                </TextWrapper>
              </>
            ) : (
              <TextWrapper>
                <BubbleText>
                  {t('reviewModal.smsPreview.noNewTiles')}
                </BubbleText>
              </TextWrapper>
            )}
          </ChatMessages>

          <BottomSection>
            <ChatInput color="black.80">
              {t('reviewModal.smsPreview.messageInputMock')}
            </ChatInput>
            <SendButton color="primary.80" variant="date">
              {t('reviewModal.smsPreview.sendMessageMock')}
            </SendButton>
          </BottomSection>
        </Content>
      </PhoneBox>
    </Wrapper>
  );
});

SmsPreview.displayName = 'SmsPreview';

const Wrapper = styled.div`
  display: flex;
  overflow: auto;
  justify-content: center;
  width: 100%;
`;

// The phone and its messages, and nothing else, so it is all the image holds.
const PhoneBox = styled.div`
  position: relative;
  display: flex;
`;

const Phone = styled(SVG)`
  height: 600px;
  width: fit-content;
`;

const BottomSection = styled.section`
  display: flex;
  padding: 20px;
`;

const ChatInput = styled(Text)`
  margin: 0 auto 0 0;
  font-size: 12px;
`;

const SendButton = styled(Text)`
  font-size: 12px;
`;

const BubbleText = styled(Text).attrs(
  defaultAttrs({
    as: 'div',
    variant: 'small2',
  }),
)`
  padding: 12px 20px 10px 20px;
  margin: 10px 0;
`;

const Headline = styled(BubbleText)`
  padding-bottom: 0;
`;

const TextWrapper = styled.div`
  margin: 0 16px;
  position: relative;
  color: white;
  border-radius: 25px 25px 25px 0;
  background: #e5e5ea;
  color: black;

  &:before {
    content: '';
    position: absolute;
    z-index: 2;
    bottom: -2px;
    left: -7px;
    height: 19px;
    border-left: 20px solid #e5e5ea;
    border-bottom-right-radius: 16px 14px;
    -webkit-transform: translate(0, -2px);
    transform: translate(0, -2px);
    border-bottom-right-radius: 15px 0px;
    transform: translate(-1px, -2px);
  }
  &:after {
    content: '';
    position: absolute;
    z-index: 3;
    bottom: -2px;
    left: 4px;
    width: 26px;
    height: 20px;
    background: white;
    border-bottom-right-radius: 10px;
    -webkit-transform: translate(-30px, -2px);
    transform: translate(-30px, -2px);
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 195px;
  object-fit: cover;
  border-top-right-radius: 25px;
  border-top-left-radius: 25px;
`;

const ChatMessages = styled.div`
  display: block;
  position: relative;
  height: 85%;
  width: 100%;
  padding: 2% 3%;
  border-bottom: 1px solid #ecf0f1;
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  height: 90%;
  width: 260px;
  cursor: default;
  margin: auto;
`;
